"use client";

import React from "react";
import { Loader, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
export default function CateringInquiry() {
  return <section data-section-id="234988" id="catering-inquiry" className="bg-background text-foreground py-24 px-4 min-h-screen flex items-center justify-center">
      <div className="w-full max-w-[600px] mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-7xl font-default font-bold mb-4 tracking-tighter uppercase">
            Book the Truck
          </h2>
          <p className="text-muted-foreground text-xl font-sans tracking-wide">
            Bring the Rolling Tacos experience to your next event. Tell us where the party is.
          </p>
        </div>

        <FormProvider formKey="catering_inquiry_truck_booking" formId="catering_inquiry_truck_booking" sectionName="Catering Inquiry" defaultValues={{
        name: "",
        email: "",
        phone: "",
        preferred_date: "",
        message: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          minLength: 10
        }
      }} submitText="Send Request" successMessage="Request received! We'll check the truck's schedule and get back to you ASAP.">
          <ConsultationForm />
        </FormProvider>
      </div>
    </section>;
}
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border-2 border-primary bg-card text-center space-y-6 animate-in fade-in duration-500 shadow-3xl">
        <div className="flex justify-center">
          <CircleCheck className="size-16 text-primary" />
        </div>
        <h3 className="text-4xl font-default font-bold uppercase tracking-tighter">Locked In</h3>
        <p className="text-muted-foreground font-sans text-lg">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-md px-8 font-default uppercase tracking-widest">
          Submit Another Request
        </Button>
      </div>;
  }
  const ghostInputClasses = "peer flex w-full bg-transparent border-0 border-b-2 border-border px-0 py-3 text-lg shadow-none outline-none transition-all focus-visible:border-primary focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 rounded-md font-mono text-foreground";
  const floatingLabelClasses = "absolute left-0 -top-5 text-sm text-primary font-default uppercase tracking-widest transition-all duration-200 peer-placeholder-shown:top-3 peer-placeholder-shown:text-lg peer-placeholder-shown:text-muted-foreground peer-focus:-top-5 peer-focus:text-sm peer-focus:text-primary pointer-events-none";
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <FormField control={control} name="name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem className="relative mt-8">
            <FormControl>
              <Input {...field} placeholder=" " className={ghostInputClasses} />
            </FormControl>
            <FormLabel className={floatingLabelClasses}>Full Name</FormLabel>
            <div className="absolute -bottom-6 left-0 w-full flex justify-between items-start">
              <FormDescription className="text-[11px] text-muted-foreground/60 font-mono uppercase">
                Who are we talking to?
              </FormDescription>
              <FormMessage className="text-[11px] font-mono" />
            </div>
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem className="relative mt-8">
            <FormControl>
              <Input type="email" {...field} placeholder=" " className={ghostInputClasses} />
            </FormControl>
            <FormLabel className={floatingLabelClasses}>Email Address</FormLabel>
            <div className="absolute -bottom-6 left-0 w-full flex justify-between items-start">
              <FormDescription className="text-[11px] text-muted-foreground/60 font-mono uppercase">
                For the booking confirmation.
              </FormDescription>
              <FormMessage className="text-[11px] font-mono" />
            </div>
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem className="relative mt-8">
            <FormControl>
              <Input type="tel" {...field} placeholder=" " className={ghostInputClasses} />
            </FormControl>
            <FormLabel className={floatingLabelClasses}>Phone Number</FormLabel>
            <div className="absolute -bottom-6 left-0 w-full flex justify-between items-start">
              <FormDescription className="text-[11px] text-muted-foreground/60 font-mono uppercase">
                Best line for a quick chat.
              </FormDescription>
              <FormMessage className="text-[11px] font-mono" />
            </div>
          </FormItem>} />

      <FormField control={control} name="preferred_date" render={({
      field
    }) => <FormItem className="relative mt-8">
            <FormLabel className="text-sm text-primary font-default uppercase tracking-widest mb-1 block">
              Event Date (Optional)
            </FormLabel>
            <FormControl>
              <Input type="date" {...field} className={`${ghostInputClasses} text-foreground/80`} />
            </FormControl>
            <div className="absolute -bottom-6 left-0 w-full flex justify-between items-start">
              <FormDescription className="text-[11px] text-muted-foreground/60 font-mono uppercase">
                When do you need the tacos?
              </FormDescription>
              <FormMessage className="text-[11px] font-mono" />
            </div>
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="relative mt-8">
            <FormControl>
              <Textarea {...field} placeholder=" " className={`${ghostInputClasses} min-h-[120px] resize-none`} />
            </FormControl>
            <FormLabel className={floatingLabelClasses}>Event Details</FormLabel>
            <div className="absolute -bottom-6 left-0 w-full flex justify-between items-start">
              <FormDescription className="text-[11px] text-muted-foreground/60 font-mono uppercase">
                Location, guest count, and taco preferences.
              </FormDescription>
              <FormMessage className="text-[11px] font-mono" />
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 border-2 border-destructive bg-destructive/10 text-destructive text-sm font-mono uppercase">
          {submitError}
        </div>}

      <div className="pt-6">
        <Button type="submit" disabled={isSubmitting} className="w-full h-16 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-2xl font-default font-bold tracking-widest uppercase transition-all duration-300 shadow-lg hover:shadow-3xl">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-3 size-6" />
              Processing...
            </> : "Send Request"}
        </Button>
      </div>
    </Form>;
}